import {
  getNextValidValue,
  getPrevValidValue,
  roundOrFloorValue,
} from './counterRules'
import type { IRoundingFlooringRules } from './counterRules'

export interface CounterRules {
  min: number
  max: number
  step: number
  roundingRules?: IRoundingFlooringRules[]
}

export interface CounterState {
  /** The committed quantity. This is what the cart sees. */
  value: number
  /** Raw keystrokes while the user types. `null` means not editing. */
  draft: string | null
}

export type CounterAction =
  | { type: 'increment' }
  | { type: 'decrement' }
  | { type: 'typeDraft'; raw: string }
  | { type: 'commitDraft' }
  | { type: 'cancelDraft' }
  | { type: 'syncExternal'; value: number }

export interface CounterView {
  /** Exactly what the input should display. */
  text: string
  value: number
  canIncrement: boolean
  canDecrement: boolean
  isEditing: boolean
}

export const initCounter = (value: number): CounterState => ({ value, draft: null })

const clamp = (n: number, { min, max }: CounterRules): number => Math.min(Math.max(n, min), max)

const nextValue = (state: CounterState, rules: CounterRules): number =>
  clamp(
    getNextValidValue({
      prevValue: state.value,
      rules: rules.roundingRules,
      step: rules.step,
      max: rules.max,
      min: rules.min,
    }),
    rules,
  )

const prevValue = (state: CounterState, rules: CounterRules): number =>
  clamp(
    getPrevValidValue({
      prevValue: state.value,
      rules: rules.roundingRules,
      step: rules.step,
      min: rules.min,
    }),
    rules,
  )

/**
 * Round a typed value up to the nearest whole step.
 *
 * `getNextValidValue` cannot be reused here: it always advances at least one
 * step, so committing a draft that is *already* a valid multiple would jump
 * past it — typing `6` with `step: 3` would land on `9`. Ceiling to the step
 * leaves a valid multiple alone and lifts anything in between.
 *
 * A non-positive step means "no step constraint"; dividing by it would produce
 * `Infinity` or `NaN`.
 */
const snapToStep = (value: number, step: number): number =>
  step > 0 ? Math.ceil(value / step) * step : value

const commit = (state: CounterState, rules: CounterRules): CounterState => {
  if (state.draft === null) return state

  const parsed = Number(state.draft)
  const isUsable = state.draft.trim() !== '' && Number.isFinite(parsed)
  if (!isUsable) return { value: state.value, draft: null }

  // Two independent constraints, applied in order: the step grid, then the
  // storefront's pallet/layer rounding rules on top of it.
  const snapped = roundOrFloorValue({
    prevValue: state.value,
    newValue: snapToStep(parsed, rules.step),
    rules: rules.roundingRules,
  })

  return { value: clamp(snapped, rules), draft: null }
}

export const counterReducer = (
  state: CounterState,
  action: CounterAction,
  rules: CounterRules,
): CounterState => {
  switch (action.type) {
    case 'increment':
      return { ...state, value: nextValue(state, rules) }
    case 'decrement':
      return { ...state, value: prevValue(state, rules) }
    case 'typeDraft':
      return { ...state, draft: action.raw }
    case 'commitDraft':
      return commit(state, rules)
    case 'cancelDraft':
      return { value: state.value, draft: null }
    case 'syncExternal':
      // Adopt the server value but never yank the input out from under the user.
      return { value: action.value, draft: state.draft }
    default: {
      const exhaustive: never = action
      return exhaustive
    }
  }
}

export const counterView = (state: CounterState, rules: CounterRules): CounterView => ({
  text: state.draft ?? String(state.value),
  value: state.value,
  canIncrement: state.value < rules.max,
  canDecrement: state.value > rules.min,
  isEditing: state.draft !== null,
})
